import React from 'react';
import PropTypes from 'prop-types';
import { Table, Typography } from 'antd';
import { resolvePackPrice } from 'pages/dashboard/DTCProductBuilder/utils/dtcVariantPricing';
import {
  collectFieldSlots,
  getStore,
  SHOPIFY_COMPARED_STORE,
} from './helpers';
import { FIELD_LABELS } from './SkuFieldTable';

const { Paragraph, Text } = Typography;

const PRICE_EPS = 0.01;

function priceEq(a, b) {
  const aEmpty = a == null || a === '' || Number(a) === 0;
  const bEmpty = b == null || b === '' || Number(b) === 0;
  if (aEmpty && bEmpty) return true;
  if (a == null || a === '' || b == null || b === '') return false;
  const x = Number(a);
  const y = Number(b);
  if (Number.isNaN(x) || Number.isNaN(y)) return false;
  return Math.abs(x - y) < PRICE_EPS;
}

function formatChangeMoney(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2);
}

function previewFieldLabel(field, slotLabel) {
  return slotLabel || FIELD_LABELS[field] || field || '';
}

function pushPreviewRow(out, row) {
  if (row.after == null || row.after === '') return;
  if (priceEq(row.before, row.after)) return;
  out.push({
    ...row,
    key: `${row.skuKey}|${row.field}|${row.platform}|${out.length}`,
  });
}

function slotIndex(sku) {
  const byField = new Map();
  (sku?.comparisonFields || []).forEach((row) => {
    collectFieldSlots(row).forEach((slot) => {
      if (!byField.has(slot.field)) byField.set(slot.field, slot);
    });
  });
  return byField;
}

function priceFor(byField, resolved, field) {
  if (resolved.has(field)) return resolved.get(field);
  return byField.get(field)?.matrix?.value;
}

function finitePrice(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Kounta cost after this apply. Current expected already includes pour size;
 * scale it when the trade price driving that cost changes.
 */
function projectedKountaCost(row, resolved) {
  const cost = row.kountaLine?.cost;
  const field = row.costDriverField;
  if (!cost || cost.applicable === false || cost.expected == null || !field) return null;
  const nextRaw = resolved.has(field) ? resolved.get(field) : row.costDriverMatrixValue;
  if (priceEq(row.costDriverMatrixValue, nextRaw)) return cost.expected;
  const oldVal = finitePrice(row.costDriverMatrixValue);
  const nextVal = finitePrice(nextRaw);
  if (oldVal == null || oldVal === 0 || nextVal == null) return null;
  return Math.round(((Number(cost.expected) * nextVal) / oldVal) * 10000) / 10000;
}

function packGridField(cell) {
  const container = String(cell.containerType || '').toLowerCase();
  if (container !== 'can') return null;
  if (Number(cell.packCount) === 4) return 'fourPackPrice';
  if (Number(cell.packCount) === 6) return 'sixPackPrice';
  return null;
}

/** After-price for a preview-only 4/6-Pack cell. Same rule as the DTC builder. */
function dtcPackAfter(cell, unit, dtcPrices) {
  const count = Number(cell.packCount);
  const hasSkuRrp = cell.packSkuRrp != null && cell.packSkuRrp !== '';
  const skus = hasSkuRrp
    ? [{
      containerType: cell.containerType,
      unitType: String(cell.unitMl ?? ''),
      packType: String(count),
      rrpPack: cell.packSkuRrp,
    }]
    : [];
  const resolvedPrice = resolvePackPrice({
    count,
    unitPrice: unit,
    unitSize: cell.unitMl,
    containerType: cell.containerType,
    skus,
    dtcPrices: dtcPrices || [],
    gridField: packGridField(cell),
  });
  const n = Number(resolvedPrice?.price);
  return Number.isFinite(n) ? n : null;
}

function shopifyAfter(cell, resolved, unitPrice, dtcPrices) {
  if (cell.previewOnly && resolved.has('rrp_unit')) {
    const unit = Number(resolved.get('rrp_unit'));
    if (!Number.isFinite(unit)) return cell.expected;
    return dtcPackAfter(cell, unit, dtcPrices);
  }
  const formula = cell.formulaLabel || '';
  if (formula === 'RRP Unit' && resolved.has('rrp_unit')) return resolved.get('rrp_unit');
  if (formula === 'RRP Pack rounded to $' && resolved.has('rrp_pack')) {
    const n = Number(resolved.get('rrp_pack'));
    return Number.isFinite(n) ? Math.round(n) : cell.expected;
  }
  const slab = formula.match(/^Unit × (\d+) × ([0-9.]+)$/);
  if (slab && resolved.has('rrp_unit')) {
    const unit = Number(resolved.get('rrp_unit'));
    if (!Number.isFinite(unit)) return cell.expected;
    return unit * Number(slab[1]) * Number(slab[2]);
  }
  if (formula === 'Unit × 12 − $12' && unitPrice != null) {
    const unit = Number(unitPrice);
    if (!Number.isFinite(unit)) return cell.expected;
    return unit * 12 - 12;
  }
  return cell.expected;
}

function shopifyPreviewRows(sku, resolved, unitPrice, byField, dtcPrices) {
  const store = getStore(sku?.shopify?.byStore, SHOPIFY_COMPARED_STORE);
  const rows = [];
  (store?.cells || []).forEach((cell) => {
    const formula = cell.formulaLabel || '';
    const previewPack = Boolean(cell.previewOnly);
    const pack12 = formula === 'Unit × 12 − $12';
    const slab = !pack12 && !previewPack && formula.startsWith('Unit ×');
    const unit = formula === 'RRP Unit';
    const pack = formula === 'RRP Pack rounded to $';
    const include = (previewPack && resolved.has('rrp_unit'))
      || (unit && resolved.has('rrp_unit'))
      || (pack && resolved.has('rrp_pack'))
      || (slab && resolved.has('rrp_unit'))
      || (pack12 && unitPrice != null);
    if (!include) return;
    const variant = cell.variantTitle
      ? `Shopify NZ · ${cell.variantTitle}`
      : 'Shopify NZ';
    const fieldLabel = previewPack
      ? (cell.formulaLabel || 'Shopify pack')
      : previewFieldLabel(cell.field, byField.get(cell.field)?.label);
    rows.push({
      skuKey: sku.skuKey,
      skuName: sku.skuName || String(sku.skuKey),
      field: fieldLabel,
      platform: variant,
      before: cell.shopifyPrice,
      after: shopifyAfter(cell, resolved, unitPrice, dtcPrices),
    });
  });
  return rows;
}

function appendSkuPreview(out, sku, resolutions, unitPrice, dtcPrices) {
  const resolved = new Map((resolutions || []).map((r) => [r.field, r.value]));
  const byField = slotIndex(sku);
  const skuName = sku.skuName || String(sku.skuKey);

  resolved.forEach((value, field) => {
    const slot = byField.get(field);
    const base = {
      skuKey: sku.skuKey,
      skuName,
      field: previewFieldLabel(field, slot?.label),
    };
    pushPreviewRow(out, {
      ...base,
      platform: 'Matrix',
      before: slot?.matrix?.value,
      after: value,
    });
    if (slot?.monday?.available) {
      pushPreviewRow(out, {
        ...base,
        platform: 'Monday',
        before: slot.monday.value,
        after: value,
      });
    }
    if (slot?.cin7?.available) {
      pushPreviewRow(out, {
        ...base,
        platform: 'Cin7',
        before: slot.cin7.value,
        after: value,
      });
    }
  });

  shopifyPreviewRows(sku, resolved, unitPrice, byField, dtcPrices).forEach((row) => {
    pushPreviewRow(out, row);
  });

  if (resolved.size === 0) return;
  (sku.comparisonFields || []).forEach((row) => {
    if (row.rowKind !== 'kounta_line') return;
    const product = row.productName || 'Kounta';
    const sellField = row.sell?.field;
    const sell = row.kountaLine?.sell;
    if (sellField && sell && sell.applicable !== false) {
      pushPreviewRow(out, {
        skuKey: sku.skuKey,
        skuName,
        field: previewFieldLabel(sellField, row.sell?.label),
        platform: `Kounta · ${product}`,
        before: sell.actual,
        after: priceFor(byField, resolved, sellField),
      });
    }
    const costField = row.costDriverField;
    if (!costField) return;
    pushPreviewRow(out, {
      skuKey: sku.skuKey,
      skuName,
      field: previewFieldLabel(costField),
      platform: `Kounta · ${product} cost`,
      before: row.kountaLine?.cost?.actual,
      after: projectedKountaCost(row, resolved),
    });
  });
}

/** One row per SKU / field / platform whose price will actually change. */
export function buildPushPreview(items, dtcPrices = []) {
  const list = items || [];
  let unitPrice = null;
  list.forEach((it) => {
    const hit = (it.resolutions || []).find((r) => r.field === 'rrp_unit');
    if (hit) unitPrice = hit.value;
  });
  const out = [];
  list.forEach((it) => {
    if (!it?.sku) return;
    appendSkuPreview(out, it.sku, it.resolutions, unitPrice, dtcPrices);
  });
  return out;
}

/** Kounta cost-only rows. Before is the current cost; after is the value being written. */
export function kountaCostPreview(sku, kountaCosts) {
  const out = [];
  const byRow = new Map();
  (sku?.comparisonFields || []).forEach((row) => {
    if (row.rowKind !== 'kounta_line' || row.kountaRowId == null) return;
    byRow.set(Number(row.kountaRowId), row);
  });
  (kountaCosts || []).forEach((item) => {
    const row = byRow.get(Number(item.kountaRowId));
    pushPreviewRow(out, {
      skuKey: sku?.skuKey,
      skuName: sku?.skuName || String(sku?.skuKey ?? ''),
      field: 'Cost',
      platform: `Kounta · ${row?.productName || item.kountaRowId} cost`,
      before: row?.kountaLine?.cost?.actual,
      after: item.value,
    });
  });
  return out;
}

export default function PushPreview({ changes }) {
  if (!changes || changes.length === 0) {
    return (
      <Text type="secondary">
        No linked platform price will change.
      </Text>
    );
  }
  const countLabel = changes.length === 1
    ? '1 price change will be pushed.'
    : `${changes.length} price changes will be pushed.`;
  return (
    <div>
      <Paragraph style={{ marginBottom: 12 }}>{countLabel}</Paragraph>
      <Table
        size="small"
        pagination={false}
        dataSource={changes}
        scroll={{ y: 420 }}
        columns={[
          {
            title: 'SKU',
            dataIndex: 'skuName',
            ellipsis: true,
            render: (name, row) => (
              <span>
                {name}
                <Text type="secondary">
                  {' #'}
                  {row.skuKey}
                </Text>
              </span>
            ),
          },
          {
            title: 'Field',
            dataIndex: 'field',
            width: 130,
            ellipsis: true,
          },
          {
            title: 'Platform',
            dataIndex: 'platform',
            width: 220,
            ellipsis: true,
          },
          {
            title: 'Before',
            dataIndex: 'before',
            width: 88,
            render: (v) => formatChangeMoney(v),
          },
          {
            title: 'After',
            dataIndex: 'after',
            width: 88,
            render: (v) => <Text strong>{formatChangeMoney(v)}</Text>,
          },
        ]}
      />
    </div>
  );
}

PushPreview.propTypes = {
  changes: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    skuKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    skuName: PropTypes.string,
    field: PropTypes.string,
    platform: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
};
